/**
 * Created by v-grfore on 23/08/2016.
 * Calculates the coordinates of each nodes of a directed cytoscape graph with the goal to paint it using svg.
 */
import { WbsLayoutLevel } from "./WbsLayoutLevel";
import { SVGHelpers, STRING_TO_MEASURE } from "../SvgHelpers";
import { CssClassAndIdNames, Names } from "../CssClassAndIdNames";
import cytoscape = require("cytoscape");
import NodeCollection = Cy.NodeCollection;
import { WbsComponent } from "../components/WbsComponent";

export interface BBoxRectangle {
  width: number;
  height: number;
}

export interface Options {
  xMin: number;
  xOffset: number;
  xChildToParentOffset: number;
  yMin: number;
  yOffset: number;
}



export class WbsLayout {
  private static minBoxWidth: number = 140;
  private static minBoxHeight: number = 70;

  private static nodeComponentBox: SVGRect;

  private static cy: Cy.Core;
  private static elements: any;
  private static graphOptions: Options;
  private static xMinDefault: number = 30;
  private static yMinDefault: number = 30;
  private static xOffsetDefault: number = 60;
  private static xChildToParentOffsetDefault: number = 20;
  private static yOffsetDefault: number = WbsLayout.minBoxHeight + 25;

  //public cy: Cy.Core;
  private static rootNode: Cy.NodeCollection;
  private static rootLayoutCollectionNodes: Cy.NodeCollection;
  private static leftestBottomChild: Cy.NodeCollection;
  private static rightestBottomChild: Cy.NodeCollection;
  private static leftestDescendantsNodes: Cy.NodeCollection;

  /**
   * 
   * @param graph 
   * @param Options 
   */
  public static calculate(graph: any, Options?: Options): any {
    WbsLayout.elements = graph; 
    WbsLayout.cy = cytoscape({
      headless: true,
      elements: WbsLayout.elements
    });

    if (Options) {
      WbsLayout.graphOptions = Options;
    } else {
      WbsLayout.graphOptions = {
        xMin: WbsLayout.xMinDefault,
        xOffset: WbsLayout.xOffsetDefault,
        xChildToParentOffset: WbsLayout.xChildToParentOffsetDefault,
        yMin: WbsLayout.yMinDefault,
        yOffset: WbsLayout.yOffsetDefault
      };
    }

    WbsLayout.rootNode = WbsLayout.cy.nodes().roots();
    WbsLayout.calculateNodesPositions();

    return WbsLayout.getWbsLayout(WbsLayout.cy);
  }

  private static calculateNodesPositions(collectionNodes?: Cy.NodeCollection): void {    

    WbsLayout.initiallizeVariables();

    //let timeStampStart = performance.now();
    WbsLayout.setNodesLayoutLevel(WbsLayout.cy);
    //let timeStampFinish = performance.now();
    //console.log(`    setNodesLayoutLevel:    ${timeStampFinish - timeStampStart} ms`);
    
    //timeStampStart = performance.now();
    WbsLayout.setAllNodeXandYcoordinates(collectionNodes);
    //timeStampFinish = performance.now();
    //console.log(`    setAllNodeXandYcoordinates:    ${timeStampFinish - timeStampStart} ms`);

    //timeStampStart = performance.now();
    WbsLayout.setAllNodeWidthAndHeight(collectionNodes);  
    //timeStampFinish = performance.now();
    //console.log(`    setAllNodeWidthAndHeight:    ${timeStampFinish - timeStampStart} ms`);
    
  }

  private static initiallizeVariables() {
    WbsLayout.nodeComponentBox = SVGHelpers.getSvgTextBoxDimensions(STRING_TO_MEASURE, CssClassAndIdNames.getClassName(Names.WbsComponent_Title));
    WbsLayout.rootLayoutCollectionNodes = WbsLayout.getLayoutCollectionNodes();
  }

  public static setNodesLayoutLevel(cy: Cy.Core): void {
    let visibleNodes: NodeCollection = cy.collection("node[visibility = 'visible']");
    if (visibleNodes.length > 0) {
      visibleNodes.map((n: Cy.NodeCollection) => {
        n.data().layoutLevel = WbsLayoutLevel.getLayoutLevel(cy, n);
      });
    }
  }

  /**
   * Finds the first node used to calculate the layout
   * @param node
   * @returns {Cy.NodeCollection}
   */
  public static findFirstLayoutNode(node: Cy.NodeCollection): Cy.NodeCollection {
    return WbsLayout.getLeftestBottomNode(node);
  }


  public static nodeHasChildren(node: Cy.NodeCollection): boolean {
    return (node.children().length > 0) ? true : false;
  }

  public static nodeHasParent(node: Cy.NodeCollection): boolean {
    return (node.parent().length > 0) ? true : false;
  }

  public static nodeHasSiblings(node: Cy.NodeCollection): boolean {
    return (node.siblings().length > 0) ? true : false;
  }
  

  /**
   * Finds the leftest node the most on the bottom of a graph, recursively
   * @param node
   * @returns {Cy.NodeCollection}
   */
  public static getLeftestBottomNode(node: Cy.NodeCollection): Cy.NodeCollection {
    WbsLayout.leftestBottomChild = node;
    if (WbsLayout.nodeHasChildren(node)) {
      let childNode: Cy.NodeCollection;

      if (node.data().layoutStyle == 1) {
        childNode = node.children("node[visibility = 'visible']").last();
      } else {
        childNode = node.children("node[visibility = 'visible']").first();
      }
      WbsLayout.getLeftestBottomNode(childNode);
    }
    return WbsLayout.leftestBottomChild;
  }

  public static getRightestBottomChild(node: Cy.NodeCollection): Cy.NodeCollection {
    WbsLayout.rightestBottomChild = node;
    if (WbsLayout.nodeHasChildren(node)) {
      let childNode: Cy.NodeCollection;

      if (node.data().layoutStyle == undefined) {        
        childNode = node.children("node[visibility = 'visible']").last();
      } else {
        childNode = node.children("node[visibility = 'visible']").last();
      }
      WbsLayout.getRightestBottomChild(childNode);
    }
    return WbsLayout.rightestBottomChild;
  }

  public static getSameLevelLeftNode(node: Cy.NodeCollection, rootLayoutCollectionNodes: Cy.NodeCollection): Cy.NodeCollection {
    let sameLevelNodes: Cy.NodeCollection = rootLayoutCollectionNodes.nodes(
      `node[layoutLevel = ${node.data().layoutLevel}][visibility = 'visible']`
    );

    for (let i: number = 0; sameLevelNodes.length - 1; i++) {
      if (node.id() == sameLevelNodes[i].id()) {
        if (sameLevelNodes[i].id() == sameLevelNodes.first().id()) {
          return null;
        } else {
          return sameLevelNodes[i - 1];
        }
      }
    }
  }

  public static getSameLevelRightNode(node: Cy.NodeCollection, rootLayoutCollectionNodes: Cy.NodeCollection): Cy.NodeCollection {
    let sameLevelNodes: Cy.NodeCollection = rootLayoutCollectionNodes.nodes(
      `node[layoutLevel = ${node.data().layoutLevel}][visibility = 'visible']`
    );

    let sameLevelNodeLastId: string = sameLevelNodes.last().id();
  
    for (let i: number = 0; sameLevelNodes.length - 1; i++) {
      if (node.id() == sameLevelNodes[i].id()) {
        if (sameLevelNodes[i].id() == sameLevelNodeLastId) {
          return null;
        } else {
          return sameLevelNodes[i + 1];
        }
      }
    }
  }

  /**
     * Returns the next node to continue the layout calculation. Algorithm starts from bottom to top, left to right.
     * @param node
     * @returns {Cy.NodeCollection}
     */
  public static nextLayoutNode(node: Cy.NodeCollection): Cy.NodeCollection {
    if (WbsLayout.nodeHasParent(node)) {
      // If node has a parent
      if (node.parent().data().layoutStyle == 0) {
        // and its parent is WBS style
        // next sibling
        if (WbsLayout.siblingsAndCallingNode(node).last().id() == node.id()) {
          return node.parent();
        } else {
          let nextSibling: Cy.NodeCollection = WbsLayout.getNextSibling(node);
          return WbsLayout.getLeftestBottomNode(nextSibling);
        }
      } else {
        // and its parent is List style
        //previous sibling
        if (WbsLayout.siblingsAndCallingNode(node).first().id() == node.id()) {          
          return node.parent();
        } else {
          let previousSibling: Cy.NodeCollection = WbsLayout.getPreviousSibling(node);
          return WbsLayout.getLeftestBottomNode(previousSibling);
        }
      }
    } else {
      return null;
    }
  }

  /**
     * Returns the collection of all the nodes, in the order they must be processed to calculate the layout and then be rendered.
     * @returns {Cy.NodeCollection}
     */
  public static getLayoutCollectionNodes(startingNode?: Cy.NodeCollection): Cy.NodeCollection {
    let layoutCollectionNodes: Cy.NodeCollection = WbsLayout.cy.collection();    
    let rootNode: Cy.NodeCollection = startingNode ? startingNode : WbsLayout.rootNode;

    let layoutNode: Cy.NodeCollection = WbsLayout.findFirstLayoutNode(rootNode);

    let stopNode: Cy.NodeCollection;
    while (layoutNode) {
      if (startingNode) {
        stopNode = rootNode.nodes(`node[id ='${layoutNode.id()}' ][visibility = 'visible']`);
        if (stopNode.length == 0) {
          break;
        }
      }

      //if (layoutNode.data().visibility == 'visible') {
      layoutCollectionNodes = layoutCollectionNodes.add(layoutNode);
      //}

      layoutNode = WbsLayout.nextLayoutNode(layoutNode);
    }
    return layoutCollectionNodes;
  }

  /**
     * Returns a node's parent's children
     * @param node
     * @returns {Cy.NodeCollection}
     */
  public static siblingsAndCallingNode(node: Cy.NodeCollection): Cy.NodeCollection {
    if (WbsLayout.nodeHasParent(node)) {
      return node.parent().children();
    } else {
      return node;
    }
  }

  /**
     * Returns a node's previous sibling
     * @param node
     * @returns {Cy.NodeCollection}
     */
  public static getPreviousSibling(node: Cy.NodeCollection): Cy.NodeCollection {
    let nodeId: string = node.id();
    if (WbsLayout.nodeHasSiblings(node)) {
      let siblingsAndCallingNode: Cy.NodeCollection = WbsLayout.siblingsAndCallingNode(node);
      for (let i: number = 0; siblingsAndCallingNode.length - 1; i++) {
        if (nodeId == siblingsAndCallingNode[i].id()) {
          if (i > 0) {
            return siblingsAndCallingNode[i - 1];
          } else {
            return null;
          }
        }
      }
    } else {
      return null;
    }
  }

  /**
     * Returns a node's next sibling
     * @param node
     * @returns {Cy.NodeCollection}
     */
  public static getNextSibling(node: Cy.NodeCollection): Cy.NodeCollection {
    let nodeId: string = node.id();
    if (WbsLayout.nodeHasSiblings(node)) {
      let siblingsAndCallingNode: Cy.NodeCollection = WbsLayout.siblingsAndCallingNode(node);

      for (let i: number = 0; siblingsAndCallingNode.length - 1; i++) {
        if (nodeId == siblingsAndCallingNode[i].id()) {
          if (i < siblingsAndCallingNode.length - 1) {
            return siblingsAndCallingNode[i + 1];
          } else {
            return null;
          }
        }
      }
    } else {
      return null;
    }
  }

  /**
     *
     * @param node
     * @returns {number}
     */
  public static getSiblingIndex(node: Cy.NodeCollection): number {
    let siblingsAndCallingNode: Cy.NodeCollection = WbsLayout.siblingsAndCallingNode(node);
    for (let i: number = 0; siblingsAndCallingNode; i++) {
      if (siblingsAndCallingNode[i].id() == node.id()) {
        return i;
      }
    }
  }

  public static setAllNodeXandYcoordinates(collectionNodes?: Cy.NodeCollection) {
    let allNodes: Cy.NodeCollection = WbsLayout.getLayoutCollectionNodes(collectionNodes);
    allNodes.map((n: Cy.NodeCollection) => {
      WbsLayout.setNodeXCoordinate(n);
      WbsLayout.setNodeYCoordinate(n);
    });
  }

  /**
     * The total width of the final node, given by its text.
     * @param n
     * @returns {string}
     */
  private static getTotalWidthText(n: Cy.NodeCollection): string {
    return `${n.data().wbsId}_${n.data().title}`;
  }

  /**
     * We decide the height and width of all nodes here.
     * @param collectionNodes
     */
  public static setAllNodeWidthAndHeight(collectionNodes?: Cy.NodeCollection) {
    let allNodes: Cy.NodeCollection = WbsLayout.getLayoutCollectionNodes(collectionNodes);
    allNodes.map((n: Cy.NodeCollection) => {
      let bboxRect: BBoxRectangle = WbsLayout.getDimensions(WbsLayout.getTotalWidthText(n), WbsLayout.nodeComponentBox);
      // console.log(`node ${n.data().id}, width: ${bboxRect.width}`);
      // console.log(`${bboxRect.width}`);
      n.data().width = bboxRect.width;
      n.data().height = bboxRect.height;
    });
  }

  public static setNodeYCoordinate(node: Cy.NodeCollection): void {
    node.data().y = WbsLayout.graphOptions.yMin + WbsLayout.graphOptions.yOffset * node.data().layoutLevel;
  }

  public static setNodeXCoordinate(node: Cy.NodeCollection): number {    
    node = WbsLayout.layoutRuleMoveNodeNextToSameLevelLeftNode(node);
    node = WbsLayout.layoutRuleNodeIsListStyle(node);
    node = WbsLayout.layoutRuleNodeIsOrgStyle(node);

    return node.data().x;
  }

  private static layoutRuleMoveNodeNextToSameLevelLeftNode(node: Cy.NodeCollection): Cy.NodeCollection {
    let sameLevelLeftNode: Cy.NodeCollection = WbsLayout.getSameLevelLeftNode(node, WbsLayout.rootLayoutCollectionNodes);
    if (sameLevelLeftNode) {
      node.data().x = sameLevelLeftNode.data().x + WbsLayout.getDimensions(WbsLayout.getTotalWidthText(sameLevelLeftNode), WbsLayout.nodeComponentBox).width + WbsLayout.graphOptions.xOffset;
    } else {
      node.data().x = WbsLayout.graphOptions.xMin;
    }
    return node;
  }

  public static getDimensions(text: string, titleSVGRect: SVGRect): BBoxRectangle {
    // in the WBS Component design we want, the width of the Component Box is defined by the text, as it is the largest and only svg Component
    // that composes the WBS Component    

    let titleWidth: number = SVGHelpers.getTextWidth(text, titleSVGRect.width);
    let titleHeight: number = titleSVGRect.height;

    let width: number;
    let height: number;

    if (titleWidth + 20 < WbsLayout.minBoxWidth) {
      width = WbsLayout.minBoxWidth;
    } else {
      width = titleWidth + 20;
    }

    if (titleHeight + 20 < WbsLayout.minBoxHeight) {
      height = WbsLayout.minBoxHeight;
    } else {
      height = titleHeight + 20;
    }

    return {
      width: width,
      height: height
    };
  }

  private static layoutRuleNodeIsListStyle(node: Cy.NodeCollection): Cy.NodeCollection {
    // if node is ListStyle
    if (node.data().layoutStyle != 0) {
      // Calculate the max of the x2 coordinates of all the nodes that are on the left of the calling node's children
      //let maxX2descendantsLeftNode: number = WbsLayout.getChildrenSameLevelLeftNodeMaxX2Value(node);
      let maxX2descendantsLeftNode: number = WbsLayout.getDescendantsLeftNodesMaxX2Value(node);

      let shiftX: number;
      // If the max x2 value is <= node's x
      if (maxX2descendantsLeftNode + WbsLayout.graphOptions.xOffset < node.data().x) {
        //if (maxX2descendantsLeftNode <= node.data().x) { //Old Layout, go back there if it fails.

        // slide descendants' x position to ensure their x is = to their parent's x + offset.
        node.children().map((child: Cy.NodeCollection) => {
          const childData: any = child.data();
          if (childData.x < node.data().x + WbsLayout.graphOptions.xOffset) {
            shiftX = node.data().x - childData.x + WbsLayout.graphOptions.xChildToParentOffset;

            WbsLayout.moveNodeAndDescendantsXCoordinate(child, shiftX);
          }
        });
      } else {
        shiftX = maxX2descendantsLeftNode - node.data().x + WbsLayout.graphOptions.xOffset;
        //shiftX = maxX2descendantsLeftNode - node.data().x; //Old Layout, go back there if it fails.
        node.data().x += shiftX;

        node.children().map((child: Cy.NodeCollection) => {
          if (child.data().x < node.data().x + WbsLayout.graphOptions.xOffset) {
            let shiftXdescendants: number = node.data().x - child.data().x + WbsLayout.graphOptions.xChildToParentOffset;
            WbsLayout.moveNodeAndDescendantsXCoordinate(child, shiftXdescendants);
          }
        });

        //if (node.children().last().data().layoutStyle == 1) {
        node.children("node[layoutStyle = 0]").map((child: Cy.NodeCollection) => {
          let collectionNodesToRecalculate: Cy.NodeCollection = WbsLayout.cy.collection();
          collectionNodesToRecalculate = collectionNodesToRecalculate.add(child);
          collectionNodesToRecalculate = collectionNodesToRecalculate.add(child.descendants());
          WbsLayout.calculateNodesPositions(collectionNodesToRecalculate);
        });
        //}
      }
    }
    return node;
  }

  private static moveNodeAndDescendantsXCoordinate(n: Cy.NodeCollection, x: number) {
    n.data().x += x;
    n.descendants().map((descendant: Cy.NodeCollection) => {
      descendant.data().x += x;
    });
  }

  private static layoutRuleNodeIsOrgStyle(node: Cy.NodeCollection): Cy.NodeCollection {
    let visibleChildrenCollection: Cy.Collection = node.children("node[visibility = 'visible']");

    if (node.data().layoutStyle == 0 && visibleChildrenCollection.length > 0) {
      let xMiddleNode: number = node.data().x + WbsLayout.getDimensions(WbsLayout.getTotalWidthText(node), WbsLayout.nodeComponentBox).width / 2;
      let xMiddleChildrenSiblings: number =
        (visibleChildrenCollection.first().data().x +
          visibleChildrenCollection.last().data().x +
          WbsLayout.getDimensions(WbsLayout.getTotalWidthText(visibleChildrenCollection.last()), WbsLayout.nodeComponentBox).width) /
        2;

      let shiftX: number = 0;
      if (xMiddleNode > xMiddleChildrenSiblings) {
        shiftX = xMiddleNode - xMiddleChildrenSiblings;
        node.descendants().map((descendant: Cy.NodeCollection) => {
          descendant.data().x += shiftX;
        });
      } else {
        node.data().x += xMiddleChildrenSiblings - xMiddleNode;
      }
    }
    return node;
  }

  private static getLeftestDescendantsNodes(node: Cy.NodeCollection): Cy.NodeCollection {
    let nodeToAdd: Cy.NodeCollection;

    if (node.children("node[visibility = 'visible']").length > 0) {
      // console.log('   It has children');
      if (node.data().layoutStyle == 1) {
        // console.log('   It is layoutStyle 1, so we loop over all its children');
        node.children("node[visibility = 'visible']").map((c: Cy.NodeCollection) => {
          nodeToAdd = c;
          // console.log('Before we add anything, collection is undefined? ');
          // console.log(WbsLayout.leftestDescendantsNodes === undefined);
          if (!WbsLayout.leftestDescendantsNodes) {
            WbsLayout.leftestDescendantsNodes = nodeToAdd;
            // console.log('we initialize');
          } else {
            WbsLayout.leftestDescendantsNodes = WbsLayout.leftestDescendantsNodes.add(nodeToAdd);
            // console.log('we add');
          }
          // console.log('Parent ' + node.id() + ' is List style, added node: ' + nodeToAdd.id() + ', collection Length: ' + WbsLayout.leftestDescendantsNodes.nodes('node').length);
          WbsLayout.getLeftestDescendantsNodes(nodeToAdd);
        });
      } else {
        nodeToAdd = node.children("node[visibility = 'visible']").first();
        // console.log('   It is layoutStyle 0, so we add node ' + nodeToAdd.id());
        if (!WbsLayout.leftestDescendantsNodes) {
          WbsLayout.leftestDescendantsNodes = nodeToAdd;
        } else {
          WbsLayout.leftestDescendantsNodes = WbsLayout.leftestDescendantsNodes.add(nodeToAdd);
        }
        // console.log('Parent ' + node.id() + ' is WBS style, added node: ' + nodeToAdd.id() + ', collection Length: ' + WbsLayout.leftestDescendantsNodes.nodes('node').length);
        WbsLayout.getLeftestDescendantsNodes(nodeToAdd);
      }
      // console.log(WbsLayout.leftestDescendantsNodes.jsons());
    } else {
      return null;
    }
    // console.log('   It has no children, so we return the value of leftestDescendantsNodes: ' + WbsLayout.leftestDescendantsNodes);
    return WbsLayout.leftestDescendantsNodes;
  }

  /**
     * Given a node, returns the max x2 values of all nodes that are at the same layoutLevel as all the node's descendants that are on the left.
     * @param node
     * @returns {number}
     */
  private static getDescendantsLeftNodesMaxX2Value(node: Cy.NodeCollection): number {
    // console.log('');
    // console.log('******************************');
    // console.log('analysing node: ' + node.id());
    WbsLayout.leftestDescendantsNodes = null;
    let leftestDescendantsNodes: Cy.NodeCollection = WbsLayout.getLeftestDescendantsNodes(node);
    let sameLevelLeftNodes: Cy.NodeCollection = null;

    if (leftestDescendantsNodes) {
      leftestDescendantsNodes.map((n: Cy.NodeCollection) => {
        !sameLevelLeftNodes
          ? (sameLevelLeftNodes = WbsLayout.getSameLevelLeftNode(n, WbsLayout.rootLayoutCollectionNodes))
          : (sameLevelLeftNodes = sameLevelLeftNodes.add(WbsLayout.getSameLevelLeftNode(n, WbsLayout.rootLayoutCollectionNodes)));
      });
    }

    // if (sameLevelLeftNodes !== null ) {
    //     console.log('sameLevelLeftNodes');
    //     console.log(sameLevelLeftNodes.jsons());
    // }
    // console.log('');
    let maxX2: number = WbsLayout.graphOptions.xMin;

    if (sameLevelLeftNodes) {
      sameLevelLeftNodes.map((n: Cy.NodeCollection) => {
        let x2: number = n.data().x + WbsLayout.getDimensions(WbsLayout.getTotalWidthText(n), WbsLayout.nodeComponentBox).width;
        maxX2 = x2 > maxX2 ? x2 : maxX2;
      });
    }

    return maxX2;
  }

  public static getWbsLayout(cy: Cy.Core): any {

    const wbsComponents: any = cy.collection("node[visibility = 'visible']").map((n: Cy.NodeCollection) => {
      let nData: any = n.data();
      return ({
        wbsComponentBox: {
          nodeId: nData.id,
          x: nData.x,
          y: nData.y,
          width: nData.width,
          height: nData.height
        },
        wbsComponentTitle: {
          text: nData.title
        },
        wbsComponentId: {
          text: nData.wbsId
        },
        wbsComponentWork: nData.work,
        wbsComponentCost: nData.cost
      });
    });

    // TODO: add all x, y, width etc. properties to the edge object, so we don't have to call the same function cy.nodes(...) so many times
    const wbsEdges: any = cy.edges().map((e: Cy.EdgeCollection) => {
      const edgeData: any = e.data();
      const sourceEdgeData: any = cy.nodes(`node[id ='${edgeData.source}' ][visibility = 'visible']`).data();
      const targetEdgeData: any = cy.nodes(`node[id ='${edgeData.target}' ][visibility = 'visible']`).data();

      return ({
        edgeId: edgeData.id,
        sourceWbsComponentData: {
          nodeId: edgeData.source,
          x: sourceEdgeData.x,
          y: sourceEdgeData.y,
          width: sourceEdgeData.width,
          height: sourceEdgeData.height,
          layoutStyle: sourceEdgeData.layoutStyle
        },
        targetWbsComponentData: {
          nodeId: edgeData.target,
          x: targetEdgeData.x,
          y: targetEdgeData.y,
          width: targetEdgeData.width,
          height: targetEdgeData.height,
          layoutStyle: targetEdgeData.layoutStyle
        }
      });
    });

    return {
      wbsComponents,
      wbsEdges
    };
  }

  public static collapseChildren(n: Cy.NodeCollection): Cy.NodeCollection {
    if (n.children().length > 0) {
      // Below is synchronous batch. It is possible to do it asynchronously, check cytoscapejs online docs.
      WbsLayout.cy.batch(function() {
        //n.children()
        n.outgoers().data("toggleState", "collapsed");
      });

      WbsLayout.cy.batch(function() {
        //n.descendants()
        n.successors().data("visibility", "hidden");
      });

      return n.descendants();
    }
    return null;
  }

  public static expandChildren(n: Cy.NodeCollection): Cy.NodeCollection {
    if (n.children().length > 0) {
      // Below is synchronous batch. It is possible to do it asynchronously, check cytoscapejs online docs.
      WbsLayout.cy.batch(function() {
        //n.children()
        n.outgoers().data("toggleState", "expanded");
      });

      WbsLayout.cy.batch(function() {
        //n.descendants()
        n.successors().data("visibility", "visible");
      });
      /*n.descendants().map( (d: Cy.NodeCollection) => {
               if (d.data().toggleState == 'expanded') {

               }
            });*/
      return n.descendants();
    }
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/layout/WbsLayout.ts